import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { register, isAuthenticated } from '../services/auth';

const RegisterPage = () => {
  const { darkMode } = useContext(ThemeContext);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    // Account Information
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',

    // Personal Information
    firstName: '',
    lastName: '',
    personalPhone: '',
    personalCountryCode: '+1',
    jobTitle: '',

    // Business Information
    companyName: '',
    businessPhone: '',
    businessCountryCode: '+1',
    businessSize: '',
    industry: '',

    // Address
    address: '',
    address2: '',
    city: '',
    state: '',
    zipCode: ''
  });

  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form validation states
  const [validationErrors, setValidationErrors] = useState({});


  // US States list with abbreviations
  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  // Mapping from full state names to abbreviations (for autofill compatibility)

  // Country codes
  const COUNTRY_CODES = [
    { code: '+1', country: 'US/CA', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+91', country: 'IN', flag: '🇮🇳' },
    { code: '+86', country: 'CN', flag: '🇨🇳' },
    { code: '+81', country: 'JP', flag: '🇯🇵' },
    { code: '+49', country: 'DE', flag: '🇩🇪' },
    { code: '+33', country: 'FR', flag: '🇫🇷' },
    { code: '+39', country: 'IT', flag: '🇮🇹' },
    { code: '+61', country: 'AU', flag: '🇦🇺' },
    { code: '+52', country: 'MX', flag: '🇲🇽' }
  ];

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/');
    }
  }, [navigate]);


  // Format phone number as (xxx) xxx-xxxx with max 10 digits
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    // Limit to 10 digits maximum
    const limited = cleaned.substring(0, 10);
    const match = limited.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      let formatted = '';
      if (match[1]) formatted += `(${match[1]}`;
      if (match[1].length === 3) formatted += ') ';
      if (match[2]) formatted += match[2];
      if (match[2].length === 3) formatted += '-';
      if (match[3]) formatted += match[3];
      return formatted;
    }
    return value;
  };

  const handlePhoneKeyDown = (e) => {
    if (e.key !== 'Backspace') return;
    const input = e.target;
    // Only intervene when there's no selection (cursor, not highlight)
    if (input.selectionStart !== input.selectionEnd) return;

    const val = input.value;
    const pos = input.selectionStart;
    const charBefore = val[pos - 1];

    // If the char before the cursor is a formatting character, skip back past
    // it and delete the preceding digit instead, so backspace never gets stuck.
    if (charBefore === ' ' || charBefore === '-' || charBefore === ')') {
      e.preventDefault();
      // Walk back past any consecutive formatting chars to find the last digit
      let digitPos = pos - 1;
      while (digitPos > 0 && !/\d/.test(val[digitPos - 1])) {
        digitPos--;
      }
      if (digitPos > 0) {
        const newValue = val.slice(0, digitPos - 1) + val.slice(pos);
        const syntheticEvent = { target: { name: input.name, value: newValue } };
        handleChange(syntheticEvent);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Format phone numbers
    if (name === 'personalPhone' || name === 'businessPhone') {
      newValue = formatPhoneNumber(value);
    }

    setFormData({
      ...formData,
      [name]: newValue
    });

    // Clear validation error when field is being edited
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
  };


  const validate = () => {
    const errors = {};
    const mainErrors = [];

    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    // Confirm Email validation
    if (!formData.confirmEmail) {
      errors.confirmEmail = 'Please confirm your email';
    } else if (formData.confirmEmail !== formData.email) {
      mainErrors.push('The email addresses you entered do not match');
      errors.confirmEmail = true;
    }

    // Password validation - only show format errors in main error bar
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      mainErrors.push('Password must be at least 8 characters');
      errors.password = true;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&-])/.test(formData.password)) {
      mainErrors.push('Password must include at least one uppercase letter, one lowercase letter, one number and one special character');
      errors.password = true;
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.confirmPassword !== formData.password && formData.password && !errors.password) {
      mainErrors.push('The passwords you entered do not match');
      errors.confirmPassword = true;
    }

    // First name validation
    if (!formData.firstName) {
      errors.firstName = 'First name is required';
    }

    // Last name validation
    if (!formData.lastName) {
      errors.lastName = 'Last name is required';
    }

    // Company name validation
    if (!formData.companyName) {
      errors.companyName = 'Company name is required';
    }

    // Phone number validation (optional, but validate format if provided)
    if (formData.personalPhone && !/^\(\d{3}\) \d{3}-\d{4}$/.test(formData.personalPhone)) {
      errors.personalPhone = 'Please enter a valid phone number';
    }

    if (formData.businessPhone && !/^\(\d{3}\) \d{3}-\d{4}$/.test(formData.businessPhone)) {
      errors.businessPhone = 'Please enter a valid business phone number';
    }

    // Address validation
    if (!formData.address) {
      errors.address = 'Street address is required';
    }

    // City validation
    if (!formData.city) {
      errors.city = 'City is required';
    }

    // State validation
    if (!formData.state) {
      errors.state = 'State is required';
    }

    // ZIP code validation
    if (!formData.zipCode) {
      errors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
      errors.zipCode = 'Please enter a valid ZIP code';
    }

    setValidationErrors(errors);
    if (mainErrors.length > 0) {
      setError(mainErrors.join('. '));
    }
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate the form
    if (!validate()) {
      return;
    }

    setIsLoading(true);

    try {
      // Prepend country code to phone numbers
      const personalPhoneWithCode = formData.personalPhone
        ? `${formData.personalCountryCode} ${formData.personalPhone}`
        : undefined;
      const businessPhoneWithCode = formData.businessPhone
        ? `${formData.businessCountryCode} ${formData.businessPhone}`
        : undefined;

      // Transform form data to match backend field names
      const registrationData = {
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        phoneNumber: personalPhoneWithCode,
        businessPhone: businessPhoneWithCode,
        jobTitle: formData.jobTitle,
        businessSize: formData.businessSize,
        industry: formData.industry,
        address: formData.address,
        address2: formData.address2,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode
      };

      // Call the register function from auth service
      await register(registrationData);

      // Set flag to trigger verification modal on next page
      sessionStorage.setItem('showVerificationModal', 'true');

      // Sync AuthContext so Navbar shows logged-in state immediately
      refreshUser();

      // Navigate to homepage immediately
      navigate('/');
    } catch (err) {
      // Handle error from API
      if (err.message) {
        setError(err.message);
      } else if (typeof err === 'object') {
        // Handle validation errors from API
        let errorMessage = 'Registration failed. Please check your input.';
        if (err.errors) {
          errorMessage = Object.values(err.errors).join(' ');
        }
        setError(errorMessage);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`flex-1 flex flex-col ${darkMode ? 'dark' : ''} bg-gray-100 dark:bg-gray-900`}>
      <div className="px-4 sm:px-6 lg:px-8 flex-grow py-10">
        <div className="max-w-7xl mx-auto w-full bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6 lg:p-8 mt-6">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Create an Account</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">
                Sign in
              </Link>
            </p>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Grid Layout for Sections */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Account Information */}
              <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg border border-gray-300 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Account Information</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`mt-1 block w-full px-3 py-2 border ${validationErrors.email ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                    placeholder="john@example.com"
                    required
                    disabled={isLoading}
                  />
                  {validationErrors.email && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Confirm Email Address *
                  </label>
                  <input
                    type="email"
                    id="confirmEmail"
                    name="confirmEmail"
                    value={formData.confirmEmail}
                    onChange={handleChange}
                    className={`mt-1 block w-full px-3 py-2 border ${validationErrors.confirmEmail ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                    placeholder="john@example.com"
                    required
                    disabled={isLoading}
                  />
                  {validationErrors.confirmEmail && typeof validationErrors.confirmEmail === 'string' && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.confirmEmail}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Password *
                    </label>
                    <div className="relative mt-1">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={`block w-full px-3 py-2 pr-10 border ${validationErrors.password ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                        placeholder="••••••••"
                        required
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {validationErrors.password && typeof validationErrors.password === 'string' && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.password}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Min 8 characters, with uppercase, lowercase, number, and special character (@$!%*?&-)
                    </p>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Confirm Password *
                    </label>
                    <div className="relative mt-1">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className={`block w-full px-3 py-2 pr-10 border ${validationErrors.confirmPassword ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                        placeholder="••••••••"
                        required
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {validationErrors.confirmPassword && typeof validationErrors.confirmPassword === 'string' && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.confirmPassword}</p>
                    )}
                  </div>
                </div>
              </div>
              </div>

              {/* Personal Information */}
              <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg border border-gray-300 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Personal Information</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`mt-1 block w-full px-3 py-2 border ${validationErrors.firstName ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="John"
                      disabled={isLoading}
                    />
                    {validationErrors.firstName && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`mt-1 block w-full px-3 py-2 border ${validationErrors.lastName ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="Doe"
                      disabled={isLoading}
                    />
                    {validationErrors.lastName && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.lastName}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="personalPhone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone Number <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                  </label>
                  <div className="mt-1 flex">
                    <select
                      name="personalCountryCode"
                      value={formData.personalCountryCode}
                      onChange={handleChange}
                      className="block w-28 pl-2 pr-6 py-2 border border-r-0 border-gray-400 dark:border-gray-600 rounded-l-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none bg-no-repeat"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.15rem center',
                        backgroundSize: '1.2em 1.2em'
                      }}
                      disabled={isLoading}
                    >
                      {COUNTRY_CODES.map((cc) => (
                        <option key={cc.code} value={cc.code}>
                          {cc.flag} {cc.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="personalPhone"
                      name="personalPhone"
                      value={formData.personalPhone}
                      onChange={handleChange}
                      onKeyDown={handlePhoneKeyDown}
                      className={`block w-full px-3 py-2 border ${validationErrors.personalPhone ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-r-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="(555) 555-5555"
                      disabled={isLoading}
                    />
                  </div>
                  {validationErrors.personalPhone && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.personalPhone}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Job Title <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    id="jobTitle"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-400 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Shop Manager"
                    disabled={isLoading}
                  />
                </div>
              </div>
              </div>

              {/* Business Information */}
              <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg border border-gray-300 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Business Information</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    className={`mt-1 block w-full px-3 py-2 border ${validationErrors.companyName ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                    placeholder="My Auto Shop"
                    disabled={isLoading}
                  />
                  {validationErrors.companyName && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.companyName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="businessPhone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Business Phone <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                  </label>
                  <div className="mt-1 flex">
                    <select
                      name="businessCountryCode"
                      value={formData.businessCountryCode}
                      onChange={handleChange}
                      className="block w-28 pl-2 pr-6 py-2 border border-r-0 border-gray-400 dark:border-gray-600 rounded-l-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none bg-no-repeat"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.15rem center',
                        backgroundSize: '1.2em 1.2em'
                      }}
                      disabled={isLoading}
                    >
                      {COUNTRY_CODES.map((cc) => (
                        <option key={cc.code} value={cc.code}>
                          {cc.flag} {cc.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="businessPhone"
                      name="businessPhone"
                      value={formData.businessPhone}
                      onChange={handleChange}
                      onKeyDown={handlePhoneKeyDown}
                      className={`block w-full px-3 py-2 border ${validationErrors.businessPhone ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-r-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="(555) 555-5555"
                      disabled={isLoading}
                    />
                  </div>
                  {validationErrors.businessPhone && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.businessPhone}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="businessSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Business Size <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                    </label>
                    <select
                      id="businessSize"
                      name="businessSize"
                      value={formData.businessSize}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-400 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      disabled={isLoading}
                    >
                      <option value="">Select size</option>
                      <option value="1-5">1-5 employees</option>
                      <option value="6-10">6-10 employees</option>
                      <option value="11-25">11-25 employees</option>
                      <option value="26-50">26-50 employees</option>
                      <option value="51+">51+ employees</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="industry" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Industry <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                    </label>
                    <select
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-400 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      disabled={isLoading}
                    >
                      <option value="">Select industry</option>
                      <option value="Auto Repair">Auto Repair</option>
                      <option value="Auto Body">Auto Body</option>
                      <option value="Tire Shop">Tire Shop</option>
                      <option value="Oil Change">Oil Change</option>
                      <option value="Dealership Service">Dealership Service</option>
                      <option value="Mobile Mechanic">Mobile Mechanic</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
              </div>

              {/* Address */}
              <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg border border-gray-300 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Address</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={`mt-1 block w-full px-3 py-2 border ${validationErrors.address ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                    placeholder="123 Main Street"
                    disabled={isLoading}
                  />
                  {validationErrors.address && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.address}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="address2" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    id="address2"
                    name="address2"
                    value={formData.address2}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-400 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Apartment, suite, unit, building, floor, etc."
                    disabled={isLoading}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      City *
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={`mt-1 block w-full px-3 py-2 border ${validationErrors.city ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="New York"
                      disabled={isLoading}
                    />
                    {validationErrors.city && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.city}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      State *
                    </label>
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      disabled={isLoading}
                      className={`mt-1 block w-full px-3 py-2 border ${validationErrors.state ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                    >
                      <option value="">Select a state...</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    {validationErrors.state && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.state}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      className={`mt-1 block w-full px-3 py-2 border ${validationErrors.zipCode ? 'border-red-300' : 'border-gray-400 dark:border-gray-600'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="10001"
                      disabled={isLoading}
                    />
                    {validationErrors.zipCode && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.zipCode}</p>
                    )}
                  </div>
                </div>
              </div>
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={
                  isLoading ||
                  !formData.email ||
                  !formData.confirmEmail ||
                  !formData.password ||
                  !formData.confirmPassword ||
                  !formData.firstName ||
                  !formData.lastName ||
                  !formData.companyName ||
                  !formData.address ||
                  !formData.city ||
                  !formData.state ||
                  !formData.zipCode
                }
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-200 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
